// From https://github.com/bjoerge/debounce-promise/blob/master/index.js

import { Nullable } from './Types';

type DeferredType = {
    promise: Promise<any>;
    resolve?: (value?: any) => void;
    reject?: (value?: any) => void;
};

export function debouncePromise<ReturnType = any>(
    this: any,
    fn: (...arg: any[]) => Promise<ReturnType>,
    wait = 0,
    options = {
        leading: false,
        accumulate: false
    }
): (...arg: unknown[]) => Promise<ReturnType> {
    let lastCallAt: number;
    let deferred: Nullable<DeferredType>;
    let timer: NodeJS.Timeout;
    let pendingArgs: unknown[][] = [];

    return function debounced(this: any, ...args: unknown[]): Promise<any> {
        const currentWait: number = wait;
        const currentTime: number = new Date().getTime();

        const isCold: boolean = !lastCallAt || currentTime - lastCallAt > currentWait;

        lastCallAt = currentTime;

        if (isCold && options.leading) {
            return options.accumulate
                ? Promise.resolve(fn.call(this, [args])).then((result: any) => result[0])
                : Promise.resolve(fn.call(this, ...args));
        }

        if (deferred) {
            clearTimeout(timer);
        } else {
            deferred = defer();
        }

        pendingArgs.push(args);
        timer = setTimeout(flush.bind(this), currentWait);

        if (options.accumulate) {
            const argsIndex = pendingArgs.length - 1;
            return deferred.promise.then(results => results[argsIndex]);
        }

        return deferred.promise;
    };

    function flush(this: any) {
        const thisDeferred = deferred;
        if (!thisDeferred) return;

        clearTimeout(timer);

        Promise.resolve(
            options.accumulate ? fn.call(this, pendingArgs) : fn.apply(this, pendingArgs[pendingArgs.length - 1])
        ).then(thisDeferred.resolve, thisDeferred.reject);

        pendingArgs = [];
        deferred = null;
    }
}

function defer(): DeferredType {
    let deferredResolve: any;
    let deferredReject: any;

    const promise = new Promise((resolve, reject) => {
        deferredResolve = resolve;
        deferredReject = reject;
    });

    return {
        promise,
        resolve: deferredResolve,
        reject: deferredReject
    };
}
